import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, type LucideIcon } from 'lucide-react';

/** Shared container for public / marketing pages */
export const MKT_CONTAINER = 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8';

/** Vertical rhythm: tight on mobile, comfortable on desktop */
export const MKT_SECTION = 'py-8 sm:py-12 lg:py-16';
export const MKT_SECTION_TIGHT = 'py-6 sm:py-10 lg:py-14';

type PageHeroProps = {
  title: ReactNode;
  subtitle?: ReactNode;
  badge?: ReactNode;
  /** home = slightly larger title */
  variant?: 'page' | 'home';
  children?: ReactNode;
};

const heroPad = {
  page: 'pt-[4.5rem] pb-8 sm:pt-24 sm:pb-10 lg:pt-28 lg:pb-14',
  home: 'pt-[4.5rem] pb-10 sm:pt-24 sm:pb-12 lg:pt-32 lg:pb-20',
};

export function PageHero({
  title,
  subtitle,
  badge,
  variant = 'page',
  children,
}: PageHeroProps) {
  const isHome = variant === 'home';

  return (
    <section className={`relative overflow-hidden ${heroPad[variant]}`}>
      <div className="absolute inset-0 bg-gradient-to-b from-gray-950 via-gray-900/95 to-gray-950" />
      <div className="pointer-events-none absolute top-0 left-1/2 h-[min(420px,55vw)] w-[min(700px,90vw)] -translate-x-1/2 rounded-full bg-blue-600/8 blur-[100px]" />

      <div className={`relative ${MKT_CONTAINER}`}>
        <div className="mx-auto max-w-3xl text-center">
          {badge && <div className="mb-3 sm:mb-4">{badge}</div>}
          <h1
            className={
              isHome
                ? 'text-[1.65rem] font-bold leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl'
                : 'text-2xl font-bold leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl'
            }
          >
            {title}
          </h1>
          {subtitle && (
            <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-gray-400 sm:mt-4 sm:text-base lg:text-lg">
              {subtitle}
            </p>
          )}
          {children && <div className="mt-5 sm:mt-6">{children}</div>}
        </div>
      </div>
    </section>
  );
}

type MarketingSectionProps = {
  children: ReactNode;
  className?: string;
  id?: string;
  tone?: 'default' | 'muted' | 'elevated';
  tight?: boolean;
};

const toneClass = {
  default: 'bg-gray-950',
  muted: 'bg-gray-900/50',
  elevated: 'bg-[#0A0F1C]',
};

export function MarketingSection({
  children,
  className = '',
  id,
  tone = 'default',
  tight = false,
}: MarketingSectionProps) {
  return (
    <section
      id={id}
      className={`${tight ? MKT_SECTION_TIGHT : MKT_SECTION} ${toneClass[tone]} ${className}`}
    >
      {children}
    </section>
  );
}

type SectionHeaderProps = {
  eyebrow?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  align?: 'center' | 'left';
  /** Page title (blog listing) = h1; section titles default to h2 */
  as?: 'h1' | 'h2';
};

export function SectionHeader({ eyebrow, title, subtitle, align = 'center', as = 'h2' }: SectionHeaderProps) {
  const alignClass = align === 'center' ? 'text-center mx-auto' : 'text-left';
  const HeadingTag = as === 'h1' ? 'h1' : 'h2';
  return (
    <div className={`mb-6 sm:mb-8 lg:mb-10 max-w-3xl ${alignClass}`}>
      {eyebrow && <div className="mb-2 sm:mb-3">{eyebrow}</div>}
      <HeadingTag className="text-xl font-bold tracking-tight text-white sm:text-2xl lg:text-3xl">{title}</HeadingTag>
      {subtitle && (
        <p className="mt-2 text-sm leading-relaxed text-gray-400 sm:mt-3 sm:text-base">{subtitle}</p>
      )}
    </div>
  );
}

export function EyebrowBadge({ children, tone = 'blue' }: { children: ReactNode; tone?: 'blue' | 'green' | 'purple' | 'orange' }) {
  const tones = {
    blue: 'bg-blue-500/10 border-blue-500/20 text-blue-300',
    green: 'bg-green-500/10 border-green-500/20 text-green-300',
    purple: 'bg-purple-500/10 border-purple-500/20 text-purple-300',
    orange: 'bg-orange-500/10 border-orange-500/20 text-orange-300',
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-wide sm:text-xs ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

export function TrustPill({ icon: Icon, label }: { icon: LucideIcon; label: string }) {
  return (
    <div className="flex items-center gap-1.5 rounded-lg border border-white/5 bg-white/[0.02] px-2.5 py-1.5 sm:gap-2 sm:px-3">
      <Icon size={14} className="shrink-0 text-blue-400" />
      <span className="text-[11px] font-medium text-gray-400 sm:text-xs">{label}</span>
    </div>
  );
}

type MarketingCtaProps = {
  title: string;
  subtitle?: string;
  href?: string;
  buttonLabel: string;
};

export function MarketingCta({ title, subtitle, href = '/register', buttonLabel }: MarketingCtaProps) {
  return (
    <MarketingSection tone="muted">
      <div className={`${MKT_CONTAINER} max-w-2xl text-center`}>
        <h2 className="text-xl font-bold text-white sm:text-2xl">{title}</h2>
        {subtitle && <p className="mt-2 text-sm text-gray-400 sm:text-base">{subtitle}</p>}
        <Link
          to={href}
          className="mt-5 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-600/20 transition hover:shadow-blue-600/35 sm:mt-6 sm:px-7 sm:py-3"
        >
          {buttonLabel}
          <ArrowRight size={16} />
        </Link>
      </div>
    </MarketingSection>
  );
}

/** Card grid gap — compact on mobile */
export const MKT_GRID_GAP = 'gap-4 sm:gap-5 lg:gap-6';
export const MKT_CARD =
  'rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 transition-colors hover:border-white/10 hover:bg-white/[0.04] sm:rounded-2xl sm:p-5 lg:p-6';
